export const API_ENDPOINTS = {
  // Auth
  AUTH: {
    LOGIN: "/auth/admin/login",
    LOGOUT: "/auth/logout",
    REFRESH: "/auth/refresh-token",
    FORGOT_PASSWORD: "/auth/forgot-password",
    VERIFY_RESET_OTP: "/auth/verify-reset-otp",
    RESET_PASSWORD: "/auth/reset-password",
    PROFILE: "/auth/profile",
  },

  // Dashboard
  DASHBOARD: {
    STATS: "/admin/dashboard/stats",
    REVENUE_BREAKDOWN: "/admin/revenue/breakdown",
    ANALYTICS: "/admin/analytics",
    CHARTS: "/admin/analytics",
    RECENT_BOOKINGS: "/admin/bookings",
    CITY_ANALYTICS: "/admin/analytics",
    PAYOUT_BALANCE: "/admin/payouts/balance",
  },

  // Customers
  CUSTOMERS: {
    LIST: "/admin/customers",
    DETAIL: (id: string) => `/admin/customers/${id}`,
    BLOCK: (id: string) => `/admin/customers/${id}/block`,
    UNBLOCK: (id: string) => `/admin/customers/${id}/unblock`,
    BOOKINGS: (id: string) => `/admin/customers/${id}/bookings`,
    PAYMENTS: (id: string) => `/admin/customers/${id}/payments`,
  },

  // State-wise breakdown (shared by customers & drivers) + backfill
  USER_STATES: "/admin/user-states",
  USER_STATES_BACKFILL: "/admin/user-states/backfill",

  // Drivers
  DRIVERS: {
    LIST: "/admin/drivers",
    DETAIL: (id: string) => `/admin/drivers/${id}`,
    APPROVE: (id: string) => `/admin/drivers/${id}/approve`,
    REJECT: (id: string) => `/admin/drivers/${id}/reject`,
    SUSPEND: (id: string) => `/admin/drivers/${id}/suspend`,
    ACTIVATE: (id: string) => `/admin/drivers/${id}/activate`,
    PENDING_KYC: "/admin/drivers/pending",
    KYC: (id: string) => `/admin/drivers/${id}/kyc`,
    RESET_DATA: (id: string) => `/admin/drivers/${id}/reset-data`,
    LIVE: "/admin/drivers/live",
  },

  // Vehicles
  VEHICLES: {
    LIST: "/admin/vehicles",
    PENDING: "/admin/vehicles/pending",
    DETAIL: (id: string) => `/admin/vehicles/${id}`,
    APPROVE: (id: string) => `/admin/vehicles/${id}/approve`,
    REJECT: (id: string) => `/admin/vehicles/${id}/reject`,
  },

  // Bookings
  BOOKINGS: {
    LIST: "/admin/bookings",
    DETAIL: (id: string) => `/admin/bookings/${id}`,
    CANCEL: (id: string) => `/admin/bookings/${id}/cancel`,
    REASSIGN: (id: string) => `/admin/bookings/${id}/reassign`,
    TRACKING: (id: string) => `/admin/bookings/${id}/tracking`,
  },

  // Pricing
  PRICING: {
    LIST: "/admin/pricing-rules",
    DETAIL: (id: string) => `/admin/pricing-rules/${id}`,
    CREATE: "/admin/pricing-rules",
    SEED: "/admin/pricing-rules/seed",
  },

  // Payments
  PAYMENTS: {
    LIST: "/admin/payments",
    WITHDRAWALS: "/admin/withdrawals",
    APPROVE_WITHDRAWAL: (id: string) => `/admin/withdrawals/${id}/approve`,
    REJECT_WITHDRAWAL: (id: string) => `/admin/withdrawals/${id}/reject`,
    DRIVER_EARNINGS: "/admin/driver-earnings",
  },

  // Withdrawals
  WITHDRAWALS: {
    LIST: "/admin/withdrawals",
    APPROVE: (id: string) => `/admin/withdrawals/${id}/approve`,
    REJECT: (id: string) => `/admin/withdrawals/${id}/reject`,
  },

  // Reports
  REPORTS: {
    STATES: "/admin/reports/states",
    BOOKINGS: "/admin/reports/bookings",
    REVENUE: "/admin/reports/revenue",
    DRIVERS: "/admin/reports/drivers",
    CUSTOMERS: "/admin/reports/customers",
    GST: "/admin/reports/gst",
    GST_INVOICE: (bookingId: string) => `/admin/reports/gst/invoice/${bookingId}`,
    GST_INVOICES_PDF: "/admin/reports/gst/invoices/pdf",
    GST_REPORT_PDF: "/admin/reports/gst/report-pdf",
    EXPORT: "/admin/reports/export",
  },

  // Admin Notifications
  ADMIN_NOTIFICATIONS: {
    LIST: "/admin/notifications",
    MARK_READ: "/admin/notifications/read-all",
    CLEAR: "/admin/notifications/clear",
  },

  // Push Notifications (admin send)
  PUSH_NOTIFICATIONS: {
    BROADCAST: "/notification/broadcast",
    SEND_TO_USER: "/notification/send",
  },

  // System Settings
  SETTINGS: {
    LIST: '/admin/settings',
    UPDATE: (key: string) => `/admin/settings/${key}`,
  },

  // Admin account (self) + team management (super-admin only)
  ADMIN_ACCOUNT: {
    SEND_PASSWORD_OTP: '/admin/profile/change-password/send-otp',
    CHANGE_PASSWORD: '/admin/profile/change-password',
  },
  ADMIN_TEAM: {
    LIST: '/admin/team',
    CREATE: '/admin/team',
    STATUS: (id: string) => `/admin/team/${id}/status`,
    RESET_PASSWORD: (id: string) => `/admin/team/${id}/reset-password`,
    DELETE: (id: string) => `/admin/team/${id}`,
  },

  // Zipto Shield
  ZIPTO_SHIELD: {
    BALANCE: '/admin/zipto-shield/balance',
    TRANSACTIONS: '/admin/zipto-shield/transactions',
    WITHDRAW: '/admin/zipto-shield/withdraw',
  },

  // Coupons
  COUPONS: {
    LIST:   '/admin/coupons',
    CREATE: '/admin/coupons',
    UPDATE: (id: string) => `/admin/coupons/${id}`,
    DELETE: (id: string) => `/admin/coupons/${id}`,
    STATS:  (id: string) => `/admin/coupons/${id}/stats`,
  },

  // Referrals
  REFERRALS: {
    LIST:  '/admin/referrals',
    STATS: '/admin/referrals/stats',
  },

  // Fraud & Safety
  FRAUD: {
    REPORTS: "/admin/fraud/reports",
    RESOLVE_REPORT: (id: string) => `/admin/fraud/reports/${id}/resolve`,
    CUSTOMER_REPORTS: (id: string) => `/admin/customers/${id}/reports`,
    CUSTOMER_BLOCKS: (id: string) => `/admin/customers/${id}/blocks`,
    BLOCK_CUSTOMER: (id: string) => `/admin/customers/${id}/block`,
    UNBLOCK_CUSTOMER: (id: string) => `/admin/customers/${id}/unblock`,
    UNBLOCK_REQUESTS: "/admin/fraud/unblock-requests",
    PROCESS_UNBLOCK: (blockId: string) => `/admin/fraud/unblock-requests/${blockId}/process`,
    // Intelligence
    INTELLIGENCE_OVERVIEW: "/admin/fraud/intelligence/overview",
    DRIVER_RISK: "/admin/fraud/intelligence/driver-risk",
    FLAGGED_BOOKINGS: "/admin/fraud/intelligence/flagged-bookings",
    DRIVER_INCIDENTS: "/admin/fraud/driver-incidents",
    DRIVER_INCIDENT: (id: string) => `/admin/fraud/driver-incidents/${id}`,
    CONFIRM_DRIVER_INCIDENT: (id: string) => `/admin/fraud/driver-incidents/${id}/confirm`,
    CLEAR_DRIVER_INCIDENT: (id: string) => `/admin/fraud/driver-incidents/${id}/clear`,
  },
  // Data Management
  DATA: {
    RESET_ALL: '/admin/data/reset',
  },
} as const;
